import React, { useState, useEffect } from 'react';
import { forecast } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  LineChart, TrendingUp, TrendingDown, DollarSign, 
  Calculator, RefreshCw, Settings2, Sparkles
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar
} from 'recharts';

const ESCENARIOS = [
  { value: 'optimista', label: 'Optimista', color: 'bg-emerald-100 text-emerald-800', desc: '+15% ingresos, -5% gastos' },
  { value: 'base', label: 'Base', color: 'bg-blue-100 text-blue-800', desc: '+5% ingresos, +3% gastos' },
  { value: 'conservador', label: 'Conservador', color: 'bg-amber-100 text-amber-800', desc: '-5% ingresos, +10% gastos' },
];

const Forecast = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Parameters
  const [periodoMeses, setPeriodoMeses] = useState(6);
  const [escenario, setEscenario] = useState('base');
  const [customMode, setCustomMode] = useState(false);
  const [crecimientoIngresos, setCrecimientoIngresos] = useState(5);
  const [crecimientoGastos, setCrecimientoGastos] = useState(3);

  const fetchForecast = async () => {
    setLoading(true);
    try {
      const params = {
        periodo_meses: periodoMeses,
        escenario: customMode ? 'custom' : escenario,
      };
      
      if (customMode) {
        params.crecimiento_ingresos = crecimientoIngresos;
        params.crecimiento_gastos = crecimientoGastos;
      }
      
      const response = await forecast.get(params);
      setData(response.data);
    } catch (error) {
      toast.error('Error al generar forecast');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(value || 0);
  };

  const formatAxisValue = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const getEscenarioInfo = () => ESCENARIOS.find(e => e.value === escenario) || ESCENARIOS[1];

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="forecast-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LineChart className="w-6 h-6 text-primary" />
            Forecast Financiero
          </h1>
          <p className="text-muted-foreground text-sm">Proyecciones basadas en datos históricos</p>
        </div>
        <Button onClick={fetchForecast} disabled={loading} data-testid="refresh-forecast">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Parámetros de Proyección
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Período</Label>
              <Select value={String(periodoMeses)} onValueChange={(v) => setPeriodoMeses(Number(v))}>
                <SelectTrigger data-testid="select-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 meses</SelectItem>
                  <SelectItem value="6">6 meses</SelectItem>
                  <SelectItem value="12">12 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Escenario</Label>
              <Select 
                value={escenario} 
                onValueChange={(v) => { setEscenario(v); setCustomMode(false); }}
                disabled={customMode}
              >
                <SelectTrigger data-testid="select-scenario">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESCENARIOS.map(e => (
                    <SelectItem key={e.value} value={e.value}>
                      <span className="flex items-center gap-2">
                        {e.label}
                        <span className="text-xs text-muted-foreground">({e.desc})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="md:col-span-2 flex items-end gap-2">
              <Button 
                variant={customMode ? "default" : "outline"} 
                onClick={() => setCustomMode(!customMode)}
                className="gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Personalizado
              </Button>
              
              {customMode && (
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <Label className="text-xs">% Ingresos</Label>
                    <Input 
                      type="number" 
                      value={crecimientoIngresos}
                      onChange={(e) => setCrecimientoIngresos(parseFloat(e.target.value) || 0)}
                      className="h-9 w-24"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">% Gastos</Label>
                    <Input 
                      type="number" 
                      value={crecimientoGastos}
                      onChange={(e) => setCrecimientoGastos(parseFloat(e.target.value) || 0)}
                      className="h-9 w-24"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button onClick={fetchForecast} disabled={loading}>
              Generar Proyección
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p>Generando proyecciones...</p>
        </div>
      ) : data?.error ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">{data.error}</p>
          </CardContent>
        </Card>
      ) : data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Promedio Histórico Ingresos</p>
                    <p className="text-xl font-bold">{formatCurrency(data.historico?.promedio_ingresos)}</p>
                    <p className="text-xs text-muted-foreground">{data.historico?.meses_analizados} meses</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Promedio Histórico Egresos</p>
                    <p className="text-xl font-bold">{formatCurrency(data.historico?.promedio_egresos)}</p>
                    <p className="text-xs text-muted-foreground">{data.historico?.meses_analizados} meses</p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Ingresos Proyectados</p>
                    <p className="text-xl font-bold">{formatCurrency(data.resumen?.ingresos_total)}</p>
                    <p className="text-xs text-muted-foreground">{periodoMeses} meses</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={`border-l-4 ${data.resumen?.flujo_neto >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Flujo Neto Proyectado</p>
                    <p className={`text-xl font-bold ${data.resumen?.flujo_neto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(data.resumen?.flujo_neto)}
                    </p>
                    <Badge className={getEscenarioInfo().color}>{getEscenarioInfo().label}</Badge>
                  </div>
                  <Calculator className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Area Chart - Projections */}
          <Card>
            <CardHeader>
              <CardTitle>Proyección de Ingresos y Gastos</CardTitle>
              <CardDescription>
                Escenario: {customMode ? 'Personalizado' : getEscenarioInfo().label} | 
                Multiplicadores: Ingresos x{data.multiplicadores?.ingresos?.toFixed(2)}, Gastos x{data.multiplicadores?.gastos?.toFixed(2)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.proyecciones}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="mes_nombre" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.split(' ')[0].substring(0, 3)}
                    />
                    <YAxis 
                      tickFormatter={formatAxisValue}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => `Mes: ${label}`}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="ingresos" 
                      name="Ingresos" 
                      stroke="#10b981" 
                      fill="#10b981" 
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="gastos" 
                      name="Gastos" 
                      stroke="#ef4444" 
                      fill="#ef4444" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart - Cash Flow */}
          <Card>
            <CardHeader>
              <CardTitle>Flujo de Caja Proyectado</CardTitle>
              <CardDescription>Saldo acumulado mensual</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.proyecciones}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="mes_nombre" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.split(' ')[0].substring(0, 3)}
                    />
                    <YAxis 
                      tickFormatter={formatAxisValue}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="flujo" name="Flujo Mensual" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="saldo_acumulado" name="Saldo Acumulado" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Projection Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Proyecciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Mes</th>
                      <th className="text-right py-3 px-2">Ingresos</th>
                      <th className="text-right py-3 px-2">Gastos</th>
                      <th className="text-right py-3 px-2">Flujo</th>
                      <th className="text-right py-3 px-2">Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.proyecciones?.map((p, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2 font-medium">{p.mes_nombre}</td>
                        <td className="text-right py-3 px-2 text-emerald-600">{formatCurrency(p.ingresos)}</td>
                        <td className="text-right py-3 px-2 text-red-600">{formatCurrency(p.gastos)}</td>
                        <td className={`text-right py-3 px-2 font-medium ${p.flujo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(p.flujo)}
                        </td>
                        <td className={`text-right py-3 px-2 font-bold ${p.saldo_acumulado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(p.saldo_acumulado)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-bold">
                      <td className="py-3 px-2">TOTAL</td>
                      <td className="text-right py-3 px-2 text-emerald-600">{formatCurrency(data.resumen?.ingresos_total)}</td>
                      <td className="text-right py-3 px-2 text-red-600">{formatCurrency(data.resumen?.gastos_total)}</td>
                      <td className={`text-right py-3 px-2 ${data.resumen?.flujo_neto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(data.resumen?.flujo_neto)}
                      </td>
                      <td className="py-3 px-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Forecast;
